import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { usePlan } from "../App";

// ── Auth state cache ─────────────────────────────────────────
// Persists the last-known sign-in / plan state so the nav renders
// correctly on the very first paint, before Clerk has fetched the
// session. Cleared on sign-out.
const CACHE_KEY = "wrtt_nav";
type NavCache = { signedIn: boolean; pro: boolean };

function readCache(): NavCache | null {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) ?? "null"); }
  catch { return null; }
}

export default function Nav() {
  const { isSignedIn, isLoaded } = useAuth();
  const plan = usePlan();
  const isPro = plan === "pro";
  const location = useLocation();

  const [cache] = useState<NavCache | null>(readCache);
  const [menuOpen, setMenuOpen] = useState(false);

  // Close mobile menu on route change.
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!isLoaded) return;
    if (isSignedIn) {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ signedIn: true, pro: isPro }));
    } else {
      localStorage.removeItem(CACHE_KEY);
    }
  }, [isLoaded, isSignedIn, isPro]);

  const showSignedIn = isLoaded ? !!isSignedIn : (cache?.signedIn ?? false);
  const showPro      = isLoaded ? isPro         : (cache?.pro      ?? false);

  const planBadge = showPro
    ? <span style={proTag}>✓ Pro</span>
    : <Link to="/upgrade" style={upgradeLink}>Upgrade</Link>;

  return (
    <nav className="nav-glass">
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "14px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Link to="/" style={{ fontWeight: 700, fontSize: 17, color: "#EFF2F5", display: "flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
          WorldRTT
        </Link>

        {/* Desktop links */}
        <div className="nav-desktop-links">
          {showSignedIn ? (
            <>
              {planBadge}
              <Link to="/dashboard/monitors" style={navLink}>Monitors</Link>
              <Link to="/dashboard/status-pages" style={navLink}>Status</Link>
              <Link to="/dashboard" style={navLink}>Dashboard</Link>
              <Link to="/settings" style={navLink}>Settings</Link>
            </>
          ) : (
            <>
              <Link to="/sign-in" style={navLink}>Sign in</Link>
              <Link to="/sign-up" className="btn-primary" style={{ padding: "8px 18px", fontSize: 14, display: "inline-block", textDecoration: "none" }}>
                Sign up free
              </Link>
            </>
          )}
        </div>

        {/* Mobile: plan badge + hamburger */}
        <div className="nav-mobile-right">
          {showSignedIn && planBadge}
          <button
            className="nav-hamburger"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
          >
            {menuOpen ? "✕" : "☰"}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div className="nav-mobile-menu">
          {showSignedIn ? (
            <>
              <Link to="/dashboard/monitors">Monitors</Link>
              <Link to="/dashboard/status-pages">Status</Link>
              <Link to="/dashboard">Dashboard</Link>
              <Link to="/settings">Settings</Link>
            </>
          ) : (
            <>
              <Link to="/sign-in">Sign in</Link>
              <Link to="/sign-up">Sign up free</Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
}

const navLink: React.CSSProperties = { color: "#98A1AB", fontSize: 14, textDecoration: "none" };
const upgradeLink: React.CSSProperties = { color: "#FF2566", fontWeight: 600, fontSize: 14, textDecoration: "none" };
const proTag: React.CSSProperties = {
  fontSize: 12, fontWeight: 700, color: "#39D98A",
  background: "rgba(57,217,138,0.1)", border: "1px solid rgba(57,217,138,0.3)",
  borderRadius: 3, padding: "3px 9px", cursor: "default", userSelect: "none",
};
